import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  UserPlusIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { getUserDetail, updateUser } from "../api/users";
import { useAuth } from "../contexts/AuthContext";
import { roleHasPermission, PERMISSIONS } from "../config/adminRoles";

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: adminUser } = useAuth();
  const showUserActions = roleHasPermission(adminUser?.role, PERMISSIONS.USERS_ACTIONS);
  const queryClient = useQueryClient();
  const [loginToggleLoading, setLoginToggleLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["user-detail", userId],
    queryFn: () => getUserDetail(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updateUser(userId, payload),
    onSuccess: (_, variables) => {
      toast.success(
        variables.isActive ? "User login enabled" : "User login disabled",
      );
      queryClient.invalidateQueries(["user-detail", userId]);
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update user");
      setLoginToggleLoading(false);
    },
  });

  const handleToggleLogin = () => {
    const user = data?.user;
    if (!user) return;
    setLoginToggleLoading(true);
    updateMutation.mutate(
      { isActive: !user.isActive },
      { onSettled: () => setLoginToggleLoading(false) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <p className="text-red-500">
          Failed to load user.{" "}
          <button onClick={() => navigate("/dashboard/users")} className="underline">
            Back to Users
          </button>
        </p>
      </div>
    );
  }

  const { user, ownedOrg, ownedCompanies, invitedCompanies } = data;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={() => navigate("/dashboard/users")} className="hover:text-primary">
          Users
        </button>
        <span>›</span>
        <span className="text-gray-900 font-medium">{user.name || user.email}</span>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-2xl font-bold text-primary">
              {(user.name || user.email)?.[0]?.toUpperCase() || "?"}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{user.name || "—"}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
              <p className="text-xs text-gray-500 mt-1.5">
                <span className="font-medium text-gray-600">Client ID:</span>{" "}
                <code className="text-gray-800 break-all">{user._id || user.id}</code>
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isEmailVerified ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {user.isEmailVerified ? "Verified" : "Not verified"}
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isActive !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}
                >
                  Login {user.isActive !== false ? "enabled" : "disabled"}
                </span>
              </div>
            </div>
          </div>
          {showUserActions && (
            <button
              onClick={handleToggleLogin}
              disabled={loginToggleLoading}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                user.isActive !== false
                  ? "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
              } disabled:opacity-50`}
            >
              {loginToggleLoading ? (
                <span className="animate-spin">⏳</span>
              ) : user.isActive !== false ? (
                <>
                  <NoSymbolIcon className="h-5 w-5" />
                  Disable login
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  Enable login
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Owned Organization */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BuildingOfficeIcon className="h-5 w-5 text-primary" />
          Owned Organization
        </h2>
        {ownedOrg ? (
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="font-medium text-gray-900">{ownedOrg.name}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600">
              <span>Plan: {ownedOrg.planId || "—"}</span>
              <span>•</span>
              <span>{ownedOrg.isSubscribed ? "Subscribed" : "Not subscribed"}</span>
              {ownedOrg.payment_country && (
                <>
                  <span>•</span>
                  <span>{ownedOrg.payment_country}</span>
                </>
              )}
            </div>
            <button
              onClick={() => navigate(`/dashboard/organizations/${ownedOrg._id}`)}
              className="mt-3 text-sm text-primary hover:text-primary/80 font-medium"
            >
              View organization →
            </button>
          </div>
        ) : (
          <p className="text-gray-500">No owned organization</p>
        )}
      </div>

      {/* Owned Companies */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BuildingOffice2Icon className="h-5 w-5 text-primary" />
          Owned Companies
        </h2>
        {ownedCompanies?.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Company
                  </th>
                  <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Phone
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ownedCompanies.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-medium text-gray-900">{c.name}</td>
                    <td className="px-5 py-4 text-gray-500">{c.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No owned companies</p>
        )}
      </div>

      {/* Invited Companies — joined via invite, company assigned but not owned */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <UserPlusIcon className="h-5 w-5 text-primary" />
          Invited Companies
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Companies this user joined via invite (assigned to them but not owned).
        </p>
        {invitedCompanies?.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Company
                  </th>
                  <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Organization
                  </th>
                  <th className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invitedCompanies.map((item, idx) => (
                  <tr key={item.company?._id || idx} className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-medium text-gray-900">
                      {item.company?.name || "—"}
                    </td>
                    <td className="px-5 py-4 text-gray-500">
                      {item.organization?.name || "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {item.companyRole || "Member"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No invited companies</p>
        )}
      </div>
    </div>
  );
};

export default UserDetail;
